import { useState } from "react";
import { ApiError } from "../api/client";
import { type MediaItem, useUploads } from "../api/media";
import type { PickedImage } from "../api/types";
import { messageFor } from "../copy/errors";
import { UploadButton } from "../library/UploadButton";
import { Button, Dialog, Tabs } from "../ui";
import { type RegionDoc, type SlotDoc, fits } from "../viewer/math";
import { PICKER_GRID, PickerSkeletons, useWholeLibrary } from "./SurfacePicker";

const TABS = [
  { id: "library", label: "From Objects" },
  { id: "images", label: "Your images" },
];
export const LOGO_LINE =
  "A logo that should not fill the whole area: close this, click the object " +
  "where the logo goes, and use Place a logo here.";

/** What an image must be to fill a label or screen area exactly: the area in
 *  whole millimetres, its proportions, and the pixels at 10 px per mm. */
export function areaHint(region: RegionDoc): string {
  const [w, h] = region.size_mm;
  const ratio =
    region.aspect >= 1
      ? `${region.aspect.toFixed(1)} : 1`
      : `1 : ${(1 / region.aspect).toFixed(1)}`;
  return (
    `This area is ${Math.round(w)} × ${Math.round(h)} mm (about ${ratio}). ` +
    `An image of ${Math.round(w * 10)} × ${Math.round(h * 10)} px or larger ` +
    "with that shape fits exactly."
  );
}

/** Why a picture of these proportions cannot go on an area, or null when it
 *  can: more than a quarter off is too far to crop. */
function misfit(aspect: number, region: RegionDoc): string | null {
  if (aspect > 0 && fits(aspect, region.aspect) !== "no") return null;
  return aspect / region.aspect > 1
    ? "Too wide for this area"
    : "Too tall for this area";
}

const CARD = "block w-full rounded-lg border-0 bg-transparent p-0 text-left";
const CARD_OFF = "cursor-not-allowed opacity-50";
const CARD_PICTURE =
  "block aspect-square w-full overflow-hidden rounded-lg bg-slate-100";
const CARD_NAME = "mt-2 block break-words text-sm font-semibold text-slate-800";

function Failure({ error }: { error: unknown }) {
  return (
    <p role="alert" className="m-0 text-sm text-slate-700">
      {messageFor(error instanceof ApiError ? error.code : undefined)}
    </p>
  );
}

function LibraryArtwork(props: {
  slot: SlotDoc;
  region: RegionDoc;
  onChoose(id: string): void;
}) {
  const { slot, region } = props;
  const library = useWholeLibrary("skin");
  // Artwork of the area's type that the surface accepts: a lit picture needs
  // a screen, any other a printable surface.
  const offered = library.items.filter(
    (item) =>
      !item.stale &&
      item.region_type === region.type &&
      slot.accepts.includes(item.emissive ? "screen" : "print"),
  );
  if (library.error) return <Failure error={library.error} />;
  if (library.loading) return <PickerSkeletons />;
  if (offered.length === 0) {
    return (
      <p className="m-0 text-sm text-slate-700">
        No artwork in the library fits this area yet. Use one of your own images
        in the other tab.
      </p>
    );
  }
  return (
    <ul className={PICKER_GRID}>
      {offered.map((item) => {
        const why = misfit(item.aspect ?? 0, region);
        return (
          <li key={item.id}>
            <button
              type="button"
              disabled={why !== null}
              title={why ?? undefined}
              className={[CARD, why ? CARD_OFF : ""].join(" ")}
              onClick={() => props.onChoose(item.id)}
            >
              <span className={CARD_PICTURE}>
                {item.thumb_url ? (
                  <img
                    src={item.thumb_url}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-contain"
                  />
                ) : null}
              </span>
              <span className={CARD_NAME}>{item.name}</span>
              {item.px ? (
                <span className="block text-sm text-slate-600">
                  {item.px[0]} × {item.px[1]}
                </span>
              ) : null}
              {why ? (
                <span className="block text-sm text-slate-700">{why}</span>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** An upload the picker can show: it has a picture of its own. */
type Picture = MediaItem & { url: string };

/** The organisation's uploaded images, newest first, and a button to add
 *  one. Choosing a picture only previews it: nothing is saved here. */
function YourImages(props: {
  region: RegionDoc;
  onChoose(image: PickedImage): void;
}) {
  const { region } = props;
  const uploads = useUploads();
  if (uploads.error) return <Failure error={uploads.error} />;
  if (!uploads.data) return <PickerSkeletons />;
  const pictures = uploads.data
    .filter((item): item is Picture => item.kind === "upload" && !!item.url)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  return (
    <div className="flex flex-col gap-4">
      {/* On a phone the button is a full touch target. */}
      <div className="flex flex-wrap items-center gap-2 max-sm:[&>button]:min-h-[44px]">
        <UploadButton label="Upload an image" />
      </div>
      {pictures.length === 0 ? (
        <p className="m-0 text-sm text-slate-700">
          You have not uploaded any images yet.
        </p>
      ) : (
        <ul className={PICKER_GRID}>
          {pictures.map((item) => {
            // A picture that has not been checked yet has no size: it is
            // offered, and checked when the look is saved.
            const why =
              item.width && item.height
                ? misfit(item.width / item.height, region)
                : null;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  disabled={why !== null}
                  title={why ?? undefined}
                  className={[CARD, why ? CARD_OFF : ""].join(" ")}
                  onClick={() =>
                    props.onChoose({
                      image_url: item.url,
                      upload_id: item.id,
                      name: item.name,
                    })
                  }
                >
                  <span className={CARD_PICTURE}>
                    <img
                      src={item.url}
                      alt={item.name}
                      loading="lazy"
                      className="h-full w-full object-contain"
                    />
                  </span>
                  <span className={CARD_NAME} aria-hidden="true">
                    {item.name}
                  </span>
                  {why ? (
                    <span className="block text-sm text-slate-700">{why}</span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** "Choose artwork" for a label or screen area: the artwork of the 3D
 *  library that suits it, or one of the user's own images. A picture more
 *  than a quarter off the area's proportions is shown, disabled, with the
 *  reason. */
export function ArtworkPicker(props: {
  open: boolean;
  onClose(): void;
  /** With its label or screen area. */
  slot: SlotDoc;
  /** An artwork's id, or one of the user's images. */
  onChoose(choice: string | PickedImage): void;
}): JSX.Element {
  const region = props.slot.region;
  const [tab, setTab] = useState("library");
  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      title="Choose artwork"
      footer={
        <Button type="button" variant="ghost" onClick={props.onClose}>
          Cancel
        </Button>
      }
    >
      {region ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <p className="m-0 text-sm text-slate-700" data-testid="area-hint">
              {areaHint(region)}
            </p>
            <p className="m-0 text-sm text-slate-600" data-testid="logo-line">
              {LOGO_LINE}
            </p>
          </div>
          <Tabs tabs={TABS} active={tab} onChange={setTab}>
            {tab === "library" ? (
              <LibraryArtwork
                slot={props.slot}
                region={region}
                onChoose={props.onChoose}
              />
            ) : (
              <YourImages region={region} onChoose={props.onChoose} />
            )}
          </Tabs>
        </div>
      ) : null}
    </Dialog>
  );
}
